/** Create Music form state + server autosave (debounced PATCH, serialized so edits never arrive out of order). */
import { useCallback, useEffect, useRef, useState } from "react";
import { createProject, genreFromLegacyStyle, getProject, pickEditable, updateProject } from "@/lib/studioApi";
import { CREATOR_DRAFT_KEY } from "@/lib/lyricsHandoff";
import { safeApiError } from "@/lib/safeErrorText";

export const DRAFT_PROJECT_KEY = "elpm-studio-draft-project";
const SAVE_DELAY_MS = 900;

export const DEFAULT_FORM = {
  title: "", artist: "Apostle Moore", lyrics: "", lyrics_lock: true, genre_key: "jamaican-gospel-reggae", custom_style: "",
  bpm: null, key: null, instruments: [], structure: "auto", duration_sec: 240, vocal_style: null,
  vocal: { mode: "signature", source: "ai", voice_id: null, profile_id: null }, folder: "",
  arrangement: { type: "solo", partner: { kind: "female", profile_id: null }, parts: [] },
  engine: { model: "V6", custom_model_id: null }, start_mode: "scratch",
};

const hasContent = (f) => Boolean((f.title || "").trim() || (f.lyrics || "").trim() || f.instruments?.length || (f.start_mode && f.start_mode !== "scratch"));

/** Lyrics Writer hand-off / homepage `?style=` → a fresh form. */
export function formFromLegacy(catalog, styleParam) {
  let d = {};
  try { d = JSON.parse(localStorage.getItem(CREATOR_DRAFT_KEY) || "{}") || {}; } catch (e) { void e; }
  const form = { ...DEFAULT_FORM };
  if (d.title) form.title = String(d.title).slice(0, 120);
  if (d.lyrics) form.lyrics = String(d.lyrics);
  const genre = genreFromLegacyStyle(styleParam || d.styleKey, catalog);
  if (genre) form.genre_key = genre;
  else if (d.styleKey === "custom" && d.customStyle) { form.genre_key = "custom"; form.custom_style = String(d.customStyle).slice(0, 300); }
  if (d.voice === "female" || d.voice === "instrumental") form.vocal = { ...form.vocal, mode: d.voice };
  return form;
}

export const rememberDraftId = (id) => { try { if (id) localStorage.setItem(DRAFT_PROJECT_KEY, id); else localStorage.removeItem(DRAFT_PROJECT_KEY); } catch (e) { void e; } };
export const readDraftId = () => { try { return localStorage.getItem(DRAFT_PROJECT_KEY); } catch (e) { void e; return null; } };

export default function useProjectDraft({ initialId, catalog, styleParam, onCreated }) {
  const [projectId, setProjectId] = useState(null);
  const [form, setForm] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [save, setSave] = useState({ state: "idle", at: null, error: null });
  const [freshId, setFreshId] = useState(0);
  const [sourceAudio, setSourceAudio] = useState(null);
  const formRef = useRef(null);
  const idRef = useRef(null);
  const dirtyRef = useRef({});
  const timerRef = useRef(null);
  const chainRef = useRef(Promise.resolve());
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;

  const adopt = useCallback((p, wasResumed) => {
    const f = { ...DEFAULT_FORM, ...pickEditable(p) };
    formRef.current = f; idRef.current = p.id;
    setForm(f); setProjectId(p.id); setResumed(wasResumed); setSourceAudio(p.source_audio || null);
    setSave({ state: "saved", at: p.updated_at, error: null });
  }, []);

  useEffect(() => {
    // The URL gained ?project= because WE just created it — keep the live form.
    if (initialId && initialId === idRef.current) return undefined;
    let cancelled = false;
    const fresh = () => {
      const f = formFromLegacy(catalog, styleParam);
      formRef.current = f; idRef.current = null; dirtyRef.current = {};
      setForm(f); setProjectId(null); setResumed(false); setSave({ state: "idle", at: null, error: null }); setFreshId((n) => n + 1); setSourceAudio(null);
    };
    (async () => {
      const handoff = (() => { try { return Boolean(JSON.parse(localStorage.getItem(CREATOR_DRAFT_KEY) || "{}")?.lyrics); } catch (e) { void e; return false; } })();
      const resumeId = !initialId && !handoff && !styleParam ? readDraftId() : null;
      const id = initialId || resumeId;
      if (!id) return fresh();
      try {
        const p = await getProject(id);
        if (cancelled) return;
        if (!initialId && (p.deleted_at || p.versions_count || p.generation)) { rememberDraftId(null); return fresh(); }
        adopt(p, !initialId);
      } catch (e) {
        if (cancelled) return;
        rememberDraftId(null);
        fresh();
        if (initialId) setSave({ state: "error", at: null, error: "That project couldn't be opened — started a new one instead." });
      }
    })();
    return () => { cancelled = true; };
  }, [initialId, catalog, styleParam, adopt]);

  const runSave = useCallback(async (force) => {
    clearTimeout(timerRef.current);
    const patch = dirtyRef.current;
    dirtyRef.current = {};
    if (idRef.current && !Object.keys(patch).length) return idRef.current;
    if (!idRef.current && !force && !hasContent(formRef.current)) return null;
    setSave((s) => ({ ...s, state: "saving", error: null }));
    try {
      if (!idRef.current) {
        const p = await createProject(formRef.current);
        idRef.current = p.id;
        setProjectId(p.id);
        rememberDraftId(p.id);
        try { localStorage.removeItem(CREATOR_DRAFT_KEY); } catch (e) { void e; }
        onCreatedRef.current?.(p.id);
      } else {
        await updateProject(idRef.current, patch);
      }
      setSave({ state: "saved", at: new Date().toISOString(), error: null });
      return idRef.current;
    } catch (e) {
      dirtyRef.current = { ...patch, ...dirtyRef.current };
      setSave((s) => ({ ...s, state: "error", error: safeApiError(e, "Couldn't save — will retry on your next change.") }));
      throw e;
    }
  }, []);

  const persist = useCallback((force = false) => {
    const next = chainRef.current.catch(() => null).then(() => runSave(force));
    chainRef.current = next.catch(() => null);
    return next;
  }, [runSave]);

  const update = useCallback((patch) => {
    formRef.current = { ...formRef.current, ...patch };
    setForm(formRef.current);
    dirtyRef.current = { ...dirtyRef.current, ...patch };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { persist().catch(() => null); }, SAVE_DELAY_MS);
  }, [persist]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (Object.keys(dirtyRef.current).length && idRef.current) persist().catch(() => null);
  }, [persist]);

  // Closing the tab mid-debounce: send the pending edit with a request the browser finishes after the page is gone.
  useEffect(() => {
    const flush = () => {
      const patch = dirtyRef.current;
      if (!idRef.current || !Object.keys(patch).length) return;
      dirtyRef.current = {};
      clearTimeout(timerRef.current);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${idRef.current}`, {
        method: "PATCH", keepalive: true, credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
      }).catch(() => null);
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, []);

  const startNew = useCallback(() => {
    clearTimeout(timerRef.current);
    rememberDraftId(null);
    dirtyRef.current = {};
    const f = { ...DEFAULT_FORM };
    formRef.current = f; idRef.current = null;
    setForm(f); setProjectId(null); setResumed(false); setSave({ state: "idle", at: null, error: null }); setFreshId((n) => n + 1); setSourceAudio(null);
  }, []);

  return { form, projectId, resumed, save, update, persist, startNew, freshId, sourceAudio, setSourceAudio };
}
